/* eslint-disable jsx-a11y/label-has-for */
import classNames from 'classnames';
import { get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { Popover } from 'web-ui-components/lib/atoms/popovers';
import withAccountSelection from '../../../containers/withAccountSelection';
import FromAccount from '../../../sharedComponents/FromAccount/FromAccount';
import ToAccount from '../../../sharedComponents/ToAccount/ToAccount';
import { paymentPropTypes } from '../../../states/payments/propTypes';
import shallowEqual from '../../../utils/compare';
import { Amount, PaymentDate, PaymentType, Reference } from './Form';
import { ChapsReference } from './Form/Reference';

class Payment extends Component {
	constructor(props) {
		super(props);

		this.updateActiveRow = this.updateActiveRow.bind(this);
		this.handleAmountFocus = this.handleAmountFocus.bind(this);
	}

	componentWillMount() {
		this.props.initValidation();
	}

	shouldComponentUpdate(nextProps, nextState) {
		return !shallowEqual(this.props, nextProps) || !shallowEqual(this.state, nextState);
	}

	handleAmountFocus() {
		this.updateActiveRow();
		const { authorisation, hasTransactionLimitError } = this.props;
		if (hasTransactionLimitError(authorisation)) {
			this.props.resetPaymentAuthorisation();
		}
	}

	updateActiveRow() {
		if (this.props.hasApiCallInProgress) return;
		this.props.updateActiveRow(this.props.id);
	}

	render() {
		const {
			id,
			isFutureDatedPaymentUnsupported,
			handleRemovePayment,
			disablePaymentType,
			showRemoveButton,
			handleAmountBlur,
			authorisation = {},
			content,
			typeLabel,
			refLabel,
			fromLabel,
			toLabel,
			date,
			dateLabel,
			handleToCardClick,
			refTooltipHelpOpen,
			handleReferenceTooltip,
			handleTypeTooltip,
			typeTooltipHelpOpen,
			showType,
			isActiveRow,
			isToSingleUsePayee,
			fromAccount,
			showDate,
			templateMode,
			isRefUpdated,
			isManagePaymentEdit,
			disableToAccount,
			handleChapsRefChange,
			handleRefChange,
			handleDateChange,
			isLongRef,
			referenceMaxLength,
			hasTransactionLimitError,
			maxDate,
			paymentDates,
			handleSelectFromAccount,
			handleFromCardClick,
			showFromAccountSelector,
			isPaymentEdit,
			todaysDate,
			chapsMode,
			chapsReference,
			biocatchIdentifier,
		} = this.props;
		const wrapperClass = classNames('c-o-list-payment__row', {
			'c-o-list-payment__row--is-active': isActiveRow,
			'c-o-payee-list--from-state': showFromAccountSelector,
			'c-o-list-payment__row--no-type': !showType,
		});

		const isTransactionLimitExceeded = hasTransactionLimitError(authorisation);
		const transactionLimitExceededMessage = get(authorisation, 'error.message', '');

		const disableType = disablePaymentType || isFutureDatedPaymentUnsupported;
		const shouldShowRefWarning = templateMode && isRefUpdated;

		const isScheduledPayment = date !== todaysDate;
		const isRecurringPayment = paymentDates && paymentDates.length;
		const isReferenceFieldDisabled =
			(isRecurringPayment && !!isPaymentEdit) || (isScheduledPayment && !!isManagePaymentEdit);

		const getPopover = (
			<Popover
				id={`popover-referenceHelp-${id}`}
				className="reference-tooltip"
				placement="right"
				isOpen={refTooltipHelpOpen}
				content={content.referenceHelp}
				onClick={handleReferenceTooltip}
			>
				<Icon iconType="InfoOutlineLarge" />
			</Popover>
		);

		return (
			<div className={wrapperClass} id={id}>
				<FromAccount
					{...this.props}
					onFocus={this.updateActiveRow}
					disable={isManagePaymentEdit}
					handleSelectFromAccount={handleSelectFromAccount}
					description={content.fromAccountDescription}
					title={fromLabel}
					className="c-o-list-payment__row-cell c-o-list-payment__row-cell--from"
					htmlFor={`field-from-account-${id}`}
					showAccountSelector={showFromAccountSelector}
					handleCardClick={handleFromCardClick}
				/>
				<ToAccount
					{...this.props}
					disable={disableToAccount || !fromAccount || isToSingleUsePayee || isManagePaymentEdit}
					onFocus={this.updateActiveRow}
					description={content.toAccountDescription}
					title={toLabel}
					handleCardClick={handleToCardClick}
					className="c-o-list-payment__row-cell c-o-list-payment__row-cell--to"
					htmlFor={`field-to-account-${id}`}
				/>
				<Amount
					{...this.props}
					onFocus={this.handleAmountFocus}
					onBlur={handleAmountBlur}
					isMaxLimitExceeded={isTransactionLimitExceeded}
					maxLimitExceededMessage={transactionLimitExceededMessage}
					useValidation={!templateMode}
					biocatchIdentifier={biocatchIdentifier}
				/>
				{chapsMode ? (
					<ChapsReference
						{...this.props}
						onFocus={this.updateActiveRow}
						description={shouldShowRefWarning ? content.refUpdatedMsg : content.referenceDescription}
						title={refLabel}
						className="c-o-list-payment__row-cell c-o-list-payment__row-cell--responsive-half c-o-list-payment__row-cell--reference"
						htmlFor={`field-reference-${id}`}
						useValidation={!templateMode}
						handleRefChange={chapsReference ? handleChapsRefChange : handleRefChange}
						isLongRef={isLongRef}
						referenceMaxLength={referenceMaxLength}
						isReferenceFieldDisabled={isReferenceFieldDisabled}
					>
						{getPopover}
					</ChapsReference>
				) : (
					<Reference
						{...this.props}
						onFocus={this.updateActiveRow}
						description={shouldShowRefWarning ? content.refUpdatedMsg : content.referenceDescription}
						title={refLabel}
						className="c-o-list-payment__row-cell c-o-list-payment__row-cell--responsive-half c-o-list-payment__row-cell--reference"
						htmlFor={`field-reference-${id}`}
						useValidation={!templateMode}
						handleRefChange={handleRefChange}
						isLongRef={isLongRef}
						referenceMaxLength={referenceMaxLength}
						isReferenceFieldDisabled={isReferenceFieldDisabled}
					>
						{getPopover}
					</Reference>
				)}
				{showDate && (
					<PaymentDate
						id={id}
						{...this.props}
						onClick={this.toggleCalendar}
						onFocus={this.updateActiveRow}
						onBlur={this.onDateBlur}
						title={dateLabel}
						className="c-o-list-payment__row-cell c-o-list-payment__row-cell--responsive-half c-o-list-payment__row-cell--date"
						isDateDisabled={isFutureDatedPaymentUnsupported}
						handleDateChange={handleDateChange}
						maxDate={maxDate}
						paymentDates={paymentDates}
					/>
				)}

				{showType && (
					<PaymentType
						{...this.props}
						onFocus={this.updateActiveRow}
						description={content.paymentTypeDescription}
						title={typeLabel}
						disabled={disableType}
						className="c-o-list-payment__row-cell c-o-list-payment__row-cell--responsive-half c-o-list-payment__row-cell--type"
					>
						<Popover
							id={`popover-paymentTypeHelp-${id}`}
							className="type-tooltip"
							placement="right"
							isOpen={typeTooltipHelpOpen}
							content={content.paymentTypeHelp}
							onClick={handleTypeTooltip}
						>
							<Icon iconType="InfoOutlineLarge" />
						</Popover>
					</PaymentType>
				)}

				<div className="c-o-list-payment__row-cell c-o-list-payment__row-cell--remove">
					{showRemoveButton && (
						<Button id={`field-delete-${id}`} onClick={handleRemovePayment}>
							<Icon iconType="TrashSmall" />
						</Button>
					)}
				</div>
			</div>
		);
	}
}

Payment.defaultProps = {
	todaysDate: moment(),
	refTooltipHelpOpen: false,
	handleReferenceTooltip: () => {},
	handleTypeTooltip: () => {},
	typeTooltipHelpOpen: false,
	showType: false,
	showMaxLimitError: true,
	showDate: true,
	disableToAccount: false,
};

Payment.propTypes = {
	...paymentPropTypes,
	handleToCardClick: PropTypes.func.isRequired,
	handleSelectFromAccount: PropTypes.func.isRequired,
	handleSelectType: PropTypes.func,
	disablePaymentType: PropTypes.bool.isRequired,
	handleRemovePayment: PropTypes.func.isRequired,
	fromLabel: PropTypes.string,
	toLabel: PropTypes.string,
	amtLabel: PropTypes.string,
	refLabel: PropTypes.string,
	dateLabel: PropTypes.string,
	typeLabel: PropTypes.string,
	todaysDate: PropTypes.instanceOf(moment),
	bankId: PropTypes.string.isRequired,
	copyDateToAll: PropTypes.func,
	copyRefToAll: PropTypes.func,
	refTooltipHelpOpen: PropTypes.bool,
	handleReferenceTooltip: PropTypes.func,
	handleTypeTooltip: PropTypes.func,
	typeTooltipHelpOpen: PropTypes.bool,
	showType: PropTypes.bool,
	showMaxLimitError: PropTypes.bool,
	isToSingleUsePayee: PropTypes.bool.isRequired,
	showDate: PropTypes.bool,
	templateMode: PropTypes.bool,
	disableToAccount: PropTypes.bool,
	isLongRef: PropTypes.bool,
	referenceMaxLength: PropTypes.string,
	hasTransactionLimitError: PropTypes.func,
	maxDate: PropTypes.shape({}),
	paymentDates: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
	maxAmountForPaymentType: PropTypes.number,
};

export default withAccountSelection(Payment);
